import { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import useAuthStore from '@entities/auth/model/useAuthStore';
import DocumentDetailModal from '@pages/approval-box/components/DocumentDetailModal/DocumentDetailModal';

import {
  useGetPcApprovalDocListQuery,
  useExecuteDocumentsStateMutation,
  useSendExecutionCompleteMailMutation,
} from '@features/approval/hooks';
import { getFilterFields } from '@features/approval/components/drafter/config/filterFields';
import { tableColumns } from '@features/approval/components/drafter/config/tableColumns';
import useApprovalSection from '@features/approval/hooks/useApprovalSection';
import { useDocFormListSimpleQuery } from '@features/form-selection/hooks';
import useGetTeamQuery from '@features/team/hooks';

import createExcelFile from '@shared/lib/excel';
import { formatDate, getDefaultDateRange } from '@shared/utils/formatDate';
import { useIsMobile } from '@shared/hooks';
import ContentSectionLayout from '@shared/components/organisms/ContentSectionLayout/ContentSectionLayout';
import MainLayout from '@shared/components/organisms/MainLayout/MainLayout';
import TabContentLayout from '@shared/components/organisms/TabContentLayout/TabContentLayout';
import { MobileLayout } from '@shared/components/organisms/mobile';
import Alert from '@shared/components/atoms/Alert';

import styles from '@pages/approval-box/ExecutionPage.module.scss';
import {
  MobileExecutionContent,
  MobileDocumentDetail,
} from '@pages/approval-box/components/mobile';

/**
 * ExecutionPage 컴포넌트
 * - 시행함 페이지
 * - 시행 문서 목록 조회 및 완료 처리 기능 제공
 */
function ExecutionPage() {
  /**
   * TODO 탭의 문서 갯수는 신규 시행 문서 발생 시 증가하며 해당 문서의 상세보기를 최초 열람하는 시점에 감소
   * TODO 더블클릭 후 상세열기
   */

  // 모바일 화면 감지
  const isMobile = useIsMobile();
  /* =======================
   * 기본 조회 기간 (3개월)
   * ======================= */
  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  /* =======================
   * 🔑 searchParams (탭별)
   * ======================= */
  const [searchParams, setSearchParams] = useState({
    date1: defaultRange.start,
    date2: defaultRange.end,
  });

  const [alertState, setAlertState] = useState({
    isOpen: false,
    message: '',
    mode: 'alert',
    onConfirm: null,
  });

  // 상세문서보기 모달 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocIdx, setSelectedDocIdx] = useState(null);

  const queryClient = useQueryClient();

  /** 문서상세 모달 닫기 시 리스트 재갱신 */
  const closeDetailModal = useCallback(
    async function () {
      setIsModalOpen(false);
      setSelectedDocIdx(null);
      queryClient.invalidateQueries({ queryKey: ['pcApprovalList'] });
      queryClient.invalidateQueries({ queryKey: ['approvalList'] });
      await queryClient.refetchQueries({ queryKey: ['pcApprovalList'] });
      await queryClient.refetchQueries({ queryKey: ['approvalList'] });
    },
    [queryClient],
  );

  function showAlert({ message, mode = 'alert', onConfirm = null }) {
    setAlertState({
      isOpen: true,
      message,
      mode,
      onConfirm,
    });
  }
  /** 로그인된 사용자 회사 코드 조회  */
  const companyCode = useAuthStore((state) => state.companyCode);

  // --- Data Fetching & Options ---

  /** 양식 목록 조회 (Simple 버전 - form_data 제외) */
  const { data: docFormList = [] } = useDocFormListSimpleQuery(companyCode);

  /** 필터용 문서 양식 옵션 (Memoized) */
  const formOptions = useMemo(
    () => [
      { label: '전체', value: '' },
      ...docFormList.map((item) => ({ label: item.title, value: item.doc })),
    ],
    [docFormList],
  );

  /** 회사 팀 목록 조회 */
  const { data: teamResponse } = useGetTeamQuery(companyCode);

  /** 필터용 팀 선택 옵션 (Memoized) */
  const teamOption = useMemo(() => {
    const teamList = Array.isArray(teamResponse?.resultData?.teamList)
      ? teamResponse.resultData.teamList
      : [];

    return [
      { label: '전체', value: '' },
      ...teamList.map((item) => ({
        label: item.team,
        value: item.teamCode,
      })),
    ];
  }, [teamResponse]);

  // --- Helpers & Handlers ---

  /** Excel 날짜 포맷터 */
  const excelFormatter = useCallback(function (rows) {
    return rows.map(function (r) {
      return {
        ...r,
        date: formatDate(r.date),
        exeState:
          r.exeState === 1 || r.exeState === '1'
            ? '완료'
            : r.exeState === 0 || r.exeState === '0'
              ? '미완료'
              : '알 수 없음',
        corpSealUsed: r.is_corp_seal_used === 1 || r.is_corp_seal_used === '1' ? 'Y' : 'N',
      };
    });
  }, []);
  /**
   * 문서 필터 구성 확장 함수
   * - 기본 필터(docType 등) 뒤에 '기안부서(team)' 필드를 동적으로 추가
   */
  const getExtendedFilterFields = useCallback(
    (filterValues, currentFormOptions) => {
      const baseFields = getFilterFields(filterValues, currentFormOptions);
      const docTypeIndex = baseFields.findIndex((f) => f.key === 'docType');

      return [
        ...baseFields.slice(0, docTypeIndex + 1),
        {
          key: 'team',
          type: 'select',
          label: '기안부서',
          value: filterValues.team || null,
          options: teamOption,
        },
        ...baseFields.slice(docTypeIndex + 1),
      ];
    },
    [teamOption],
  );

  // --- Table Configuration ---

  const extendedTableColumns = useMemo(
    () => [
      {
        key: 'radio',
        width: '40px',
        className: styles.radioCell,
        sortable: false,
        stopPropagation: true,
        // Note: DOM 직접 접근 방식은 추후 React 상태 기반으로 리팩토링 권장
        headerRender: () => (
          <input
            type="checkbox"
            name="completeRadio"
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              const checked = e.target.checked;
              const rowCheckboxes = document.querySelectorAll('input[name^="row-"]');
              rowCheckboxes.forEach((cb) => (cb.checked = checked));
            }}
          />
        ),
        render: (_, rowItem) => (
          <input type="checkbox" name={`row-${rowItem.idx}`} onClick={(e) => e.stopPropagation()} />
        ),
      },
      {
        key: 'exeState',
        label: '완료여부',
        width: '90px',
        className: styles.colDocumentType,
        render: (_, row) => {
          const value = row.exeState;
          if (value === 1 || value === '1') return '완료';
          if (value === 0 || value === '0') return '미완료';
          return '알 수 없음';
        },
      },
      ...tableColumns,
    ],
    [],
  );
  // 2026.02.23 엑셀용 컬럼 생성
  const excelColumns = useMemo(() => {
    return [
      ...extendedTableColumns,
      {
        key: 'corpSealUsed',
        label: '법인인감사용',
        render: (_, row) =>
          row.is_corp_seal_used === 1 || row.is_corp_seal_used === '1' ? 'Y' : 'N',
      },
    ];
  }, [extendedTableColumns]);
  // --- Mutations ---

  /** 시행 상태 변경 Mutation */
  const executeSection = useApprovalSection({
    queryType: 'execute',
    searchParams: searchParams,
    setSearchParams: setSearchParams,
    filterFieldBuilder: getExtendedFilterFields,
    tableColumns: extendedTableColumns,
    useQueryHook: useGetPcApprovalDocListQuery,
    formOptions,
    excelFormatter,
    defaultSort: { field: 'date', direction: 'desc' },
  });

  const { mutate: executeState } = useExecuteDocumentsStateMutation({
    onSuccess: () => {
      executeSection.approvalRefetch();
      //  전체 체크박스 해제
      const rowCheckboxes = document.querySelectorAll('input[name^="row-"]');
      rowCheckboxes.forEach((cb) => {
        cb.checked = false;
      });

      // "전체 선택" 체크박스도 해제
      const headerCheckbox = document.querySelector('input[name="completeRadio"]');
      if (headerCheckbox) {
        headerCheckbox.checked = false;
      }
    },
    onError: (error) => {
      console.error('Mutation 실패:', error.response?.data || error);
    },
  });

  /** 시행완료 메일발송 Mutation (단건) */
  const { mutate: sendExecutionMail } = useSendExecutionCompleteMailMutation({
    onSuccess: () => {
      showAlert({ message: '메일이 발송되었습니다.', mode: 'alert' });
      // 체크박스 초기화
      const rowCheckboxes = document.querySelectorAll('input[name^="row-"]');
      rowCheckboxes.forEach((cb) => {
        cb.checked = false;
      });
      const headerCheckbox = document.querySelector('input[name="completeRadio"]');
      if (headerCheckbox) {
        headerCheckbox.checked = false;
      }
    },
    onError: (error) => {
      const msg =
        error?.response?.data?.error_msg ||
        error?.response?.data?.message ||
        '메일 발송에 실패했습니다.';
      showAlert({ message: msg, mode: 'alert' });
    },
  });

  /** 메일발송 핸들러 (단건 전용 + 완료여부 검증) */
  function handleSendMail() {
    const checkedBoxes = Array.from(document.querySelectorAll('input[name^="row-"]:checked'));
    const selectedDocNumbers = checkedBoxes.map(function (checkbox) {
      return checkbox.getAttribute('name').replace('row-', '');
    });

    // 0건
    if (selectedDocNumbers.length === 0) {
      showAlert({ message: '선택된 문서가 없습니다.', mode: 'alert' });
      return;
    }

    // 단건 전용 → 2건 이상 차단
    if (selectedDocNumbers.length > 1) {
      showAlert({ message: '메일발송은 한 건만 선택 가능합니다.', mode: 'alert' });
      return;
    }

    const docIdx = selectedDocNumbers[0];

    // 완료여부(exeState === 1) 검증
    const targetRow = (executeSection.tableData || []).find(
      (row) => String(row.idx) === String(docIdx),
    );
    const isCompleted = targetRow && (targetRow.exeState === 1 || targetRow.exeState === '1');
    if (!isCompleted) {
      showAlert({ message: '완료된 문서만 메일 발송이 가능합니다.', mode: 'alert' });
      return;
    }

    showAlert({
      message: '선택한 문서의 처리 완료 메일을 발송하시겠습니까?',
      mode: 'confirm',
      onConfirm: function () {
        sendExecutionMail(Number(docIdx));
      },
    });
  }

  /** 선택 완료 처리 핸들러 */
  function handleComplete() {
    const checkedBoxes = Array.from(document.querySelectorAll('input[name^="row-"]:checked'));
    const selectedDocNumbers = checkedBoxes.map(function (checkbox) {
      return checkbox.getAttribute('name').replace('row-', '');
    });

    if (selectedDocNumbers.length === 0) {
      showAlert({
        message: '선택된 문서가 없습니다.',
        mode: 'alert',
      });
      return;
    }

    // 선택됨 → confirm alert 띄우기
    showAlert({
      message: '완료 상태를 변경하시겠습니까?',
      mode: 'confirm',
      onConfirm: function () {
        executeState(selectedDocNumbers);
      },
    });
  }

  function closeAlert() {
    setAlertState((prev) => ({
      ...prev,
      isOpen: false,
    }));
  }

  // 행 클릭 핸들러 (코멘트 확인 API 제거 - 모달 내 useDocumentCommentListQuery에서 조회)
  function handleRowClick(item) {
    const docIdx = item.idx;
    if (!docIdx) {
      console.warn('문서 인덱스를 찾을 수 없습니다:', item);
      return;
    }

    setSelectedDocIdx(docIdx);
    setIsModalOpen(true);
  }

  // 모바일 선택 완료
  function handleMobileComplete(selectedIds) {
    if (!selectedIds || selectedIds.length === 0) {
      showAlert({
        message: '선택된 문서가 없습니다.',
        mode: 'alert',
      });
      return;
    }

    showAlert({
      message: '완료 상태를 변경하시겠습니까?',
      mode: 'confirm',
      onConfirm: () => {
        executeState(selectedIds, {
          onSuccess: () => {
            executeSection.approvalRefetch(); // 최신 데이터 반영
          },
        });
      },
    });
  }

  // 모바일 선택 메일발송 (단건 전용 + 완료여부 검증)
  function handleMobileSendMail(selectedIds) {
    if (!selectedIds || selectedIds.length === 0) {
      showAlert({ message: '선택된 문서가 없습니다.', mode: 'alert' });
      return;
    }

    if (selectedIds.length > 1) {
      showAlert({ message: '메일발송은 한 건만 선택 가능합니다.', mode: 'alert' });
      return;
    }

    const docIdx = selectedIds[0];
    const targetRow = (executeSection.tableData || []).find(
      (row) => String(row.idx) === String(docIdx),
    );
    const isCompleted = targetRow && (targetRow.exeState === 1 || targetRow.exeState === '1');
    if (!isCompleted) {
      showAlert({ message: '완료된 문서만 메일 발송이 가능합니다.', mode: 'alert' });
      return;
    }

    showAlert({
      message: '선택한 문서의 처리 완료 메일을 발송하시겠습니까?',
      mode: 'confirm',
      onConfirm: () => sendExecutionMail(Number(docIdx)),
    });
  }

  // 모바일 레이아웃 렌더링
  if (isMobile) {
    return (
      <MobileLayout title="시행문서">
        <MobileExecutionContent
          executeSection={executeSection}
          onItemClick={handleRowClick}
          formOptions={formOptions}
          teamOptions={teamOption}
          onComplete={handleMobileComplete}
          onSendMail={handleMobileSendMail}
        />
        {/* 모바일 문서 상세 (PC와 다르게 본문 이미지 사용) */}
        <MobileDocumentDetail
          isOpen={isModalOpen}
          onClose={closeDetailModal}
          docIdx={selectedDocIdx}
          userRole="executor"
          tabType="execute"
        />
        {/* Alert 컴포넌트 */}
        <Alert
          isOpen={alertState.isOpen}
          message={alertState.message}
          mode={alertState.mode}
          onConfirm={alertState.onConfirm}
          onClose={closeAlert}
        />
      </MobileLayout>
    );
  }

  // PC 레이아웃 렌더링
  return (
    <MainLayout>
      <TabContentLayout
        tabs={{
          label: '시행함',
          content: (
            <ContentSectionLayout
              {...executeSection}
              classes={styles}
              showExcelButton={true}
              emptyMessage="현재 시행 문서가 없습니다."
              onExcel={() =>
                createExcelFile({
                  rows: executeSection.handleExcel(),
                  fileName: '시행문서.xlsx',
                  columns: excelColumns,
                })
              }
              extraContent={
                <>
                  <button className={styles.completeButton} onClick={handleComplete}>
                    완료 상태 변경
                  </button>
                  <button className={styles.mailButton} onClick={handleSendMail}>
                    메일발송
                  </button>
                </>
              }
              onRowClick={handleRowClick}
            />
          ),
        }}
      />
      {/* 공통 Alert 컴포넌트 */}
      <Alert
        isOpen={alertState.isOpen}
        message={alertState.message}
        mode={alertState.mode}
        onConfirm={alertState.onConfirm}
        onClose={closeAlert}
      />
      {/* 문서 상세 모달 */}
      <DocumentDetailModal
        isOpen={isModalOpen}
        onClose={closeDetailModal}
        docIdx={selectedDocIdx}
        userRole="executor"
        tabType="execute"
      />
    </MainLayout>
  );
}

export default ExecutionPage;
